import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Box,
    Checkbox,
    Divider,
    FormControlLabel,
    IconButton,
    Paper,
    Slider,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import HeightIcon from "@mui/icons-material/Height";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { panRange, snapToStep, type YBounds } from "utils/charts/sharedYDomain";

interface Props {
    /** 描画中データ全体の全域。undefined なら操作不可（有効値なし）。 */
    bounds: YBounds | undefined;
    /** 現在の選択範囲。 */
    range: YBounds | undefined;
    onChange: (range: YBounds) => void;
    step: number;
    unified: boolean;
    onUnifiedChange: (unified: boolean) => void;
}

// 上下ボタン 1 回の移動量（step の倍数＝全域の約 5%）。
const NUDGE_STEPS = 10;

// テキスト入力の確定時に許容する最小の刻み。全域が広いデータでは
// スライダーの step（全域 / 200）が 0.1 より粗くなり、テキスト入力でも
// その粗い刻みにスナップされてしまうため、テキスト入力だけは
// これより細かい刻みを保証する（スライダーのドラッグ挙動は変えない）。
const TEXT_INPUT_MIN_STEP = 0.1;

// 軸端の表示は桁に応じて丸める（横並びの読み取り値のため桁数を抑える）。
const formatBound = (value: number): string => {
    const abs = Math.abs(value);
    if (abs !== 0 && abs < 0.01) return value.toPrecision(2);
    if (abs >= 1000) return value.toFixed(0);
    if (abs >= 10) return value.toFixed(1);
    return value.toFixed(2);
};

const readoutInputSx = {
    width: 64,
    "& input": {
        textAlign: "right",
        fontVariantNumeric: "tabular-nums",
        fontWeight: 600,
        fontSize: "0.875rem",
    },
} as const;

/**
 * 条件全体表示グリッドの縦軸レンジを全グラフ一括で絞り込むスライダー。
 * recharts の Brush は x 専用のため、縦軸は MUI Slider で同等の操作を提供する。
 * 表示範囲のみを変え、データ・API リクエスト・系列定義は変えない。
 *
 * 操作:
 * - 両端の thumb: 上下限を個別に変更（クリック / ドラッグ / キーボード）
 * - 選択帯（thumb 間）のドラッグ: 幅を保ったまま範囲を平行移動
 * - 上下ボタン: 同じ平行移動をキーボードでも行えるようにするための代替手段
 */
export const YAxisRangeSlider: React.FC<Props> = ({
    bounds,
    range,
    onChange,
    step,
    unified,
    onUnifiedChange,
}) => {
    const disabled = !unified || !bounds;
    const min = bounds ? bounds[0] : 0;
    const max = bounds ? bounds[1] : 1;
    const value: YBounds = range ?? [min, max];
    // 全域いっぱいのときは平行移動する余地が無い（thumb 操作のみ）。
    const canPan = !disabled && value[1] - value[0] < max - min;

    const sliderRef = useRef<HTMLSpanElement>(null);
    // ドラッグ開始時点の値を保持し、累積移動量から範囲を決める（1 回ごとの加算では誤差が溜まる）。
    const panStartRef = useRef<{ x: number; range: YBounds; valuePerPx: number } | null>(null);
    const [panning, setPanning] = useState(false);

    const handleSliderChange = (_: Event, next: number | number[]) => {
        if (!Array.isArray(next) || next.length !== 2) return;
        onChange([next[0], next[1]]);
    };

    const nudge = (steps: number) => {
        if (!bounds) return;
        onChange(panRange(value, bounds, step * steps, step));
    };

    // 数値入力欄。入力中はローカル文字列で保持し、確定（blur / Enter）時にのみ onChange を呼ぶ
    // （1 文字ごとに反映すると未完成の入力値でスライダーが暴れるため）。
    const [lowerText, setLowerText] = useState<string | null>(null);
    const [upperText, setUpperText] = useState<string | null>(null);

    const textStep = Math.min(step, TEXT_INPUT_MIN_STEP);

    const commitLower = () => {
        const text = lowerText;
        setLowerText(null);
        if (text === null || !bounds) return;
        const parsed = Number(text);
        if (!Number.isFinite(parsed)) return;
        const [lo, hi] = bounds;
        const clamped = Math.min(Math.max(parsed, lo), Math.min(value[1], hi));
        onChange([snapToStep(clamped, textStep), value[1]]);
    };

    const commitUpper = () => {
        const text = upperText;
        setUpperText(null);
        if (text === null || !bounds) return;
        const parsed = Number(text);
        if (!Number.isFinite(parsed)) return;
        const [lo, hi] = bounds;
        const clamped = Math.max(Math.min(parsed, hi), Math.max(value[0], lo));
        onChange([value[0], snapToStep(clamped, textStep)]);
    };

    const handleBoundKeyDown = (
        e: React.KeyboardEvent<HTMLDivElement>,
        cancel: () => void
    ) => {
        // onKeyDown は MUI の型上 currentTarget が div になるが、blur は実際に
        // フォーカスしている要素（target）に対して呼ぶ必要がある。
        const target = e.target as HTMLElement;
        if (e.key === "Enter") {
            target.blur();
        } else if (e.key === "Escape") {
            cancel();
            target.blur();
        }
    };

    // 選択帯の pointerdown を capture で拾い、MUI 既定（近い thumb を移動）より先に平行移動へ切り替える。
    const handlePointerDownCapture = (e: React.PointerEvent<HTMLDivElement>) => {
        if (!canPan || !bounds) return;
        const root = sliderRef.current;
        if (!root) return;
        // thumb 上は既定の上下限変更に任せる。数値入力欄上はフォーカス・編集操作に任せる。
        if ((e.target as HTMLElement).closest(".MuiSlider-thumb, input")) return;
        const rect = root.getBoundingClientRect();
        if (rect.width === 0) return;
        const valuePerPx = (max - min) / rect.width;
        const pointerValue = min + (e.clientX - rect.left) * valuePerPx;
        if (!Number.isFinite(pointerValue)) return;
        // 選択帯の外は既定動作（近い thumb をその位置へ）を維持する。
        if (pointerValue <= value[0] || pointerValue >= value[1]) return;
        e.preventDefault();
        e.stopPropagation();
        panStartRef.current = { x: e.clientX, range: value, valuePerPx };
        setPanning(true);
    };

    const endPan = useCallback(() => {
        panStartRef.current = null;
        setPanning(false);
    }, []);

    useEffect(() => {
        if (!panning || !bounds) return;
        const onMove = (e: PointerEvent) => {
            const start = panStartRef.current;
            if (!start) return;
            const delta = (e.clientX - start.x) * start.valuePerPx;
            if (!Number.isFinite(delta)) return;
            onChange(panRange(start.range, bounds, delta, step));
        };
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", endPan);
        window.addEventListener("pointercancel", endPan);
        return () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", endPan);
            window.removeEventListener("pointercancel", endPan);
        };
    }, [panning, bounds, step, onChange, endPan]);

    const atLowerEdge = !canPan || value[0] <= min;
    const atUpperEdge = !canPan || value[1] >= max;

    return (
        <Paper
            variant="outlined"
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1.5,
                px: 1.5,
                py: 1,
                borderRadius: 2,
                bgcolor: "background.paper",
            }}
        >
            <FormControlLabel
                control={
                    <Checkbox
                        size="small"
                        checked={unified}
                        onChange={(e) => onUnifiedChange(e.target.checked)}
                    />
                }
                label="縦軸を統一"
                sx={{ mr: 0 }}
            />

            <Divider orientation="vertical" flexItem />

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: disabled ? "text.disabled" : "text.secondary" }}>
                <HeightIcon fontSize="small" />
                <Typography variant="body2">縦軸範囲</Typography>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                <Box
                    onPointerDownCapture={handlePointerDownCapture}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        userSelect: panning ? "none" : undefined,
                    }}
                >
                    <TextField
                        size="small"
                        variant="standard"
                        disabled={disabled}
                        value={lowerText ?? (bounds ? formatBound(value[0]) : "-")}
                        onChange={(e) => setLowerText(e.target.value)}
                        onFocus={() => setLowerText(bounds ? formatBound(value[0]) : "")}
                        onBlur={commitLower}
                        onKeyDown={(e) => handleBoundKeyDown(e, () => setLowerText(null))}
                        slotProps={{ htmlInput: { "aria-label": "縦軸下限を入力" } }}
                        sx={readoutInputSx}
                    />

                    <Tooltip
                        title={
                            canPan
                                ? "両端で上下限を変更 / 中央をドラッグで範囲を平行移動"
                                : "両端のつまみで上下限を変更"
                        }
                    >
                        <Slider
                            ref={sliderRef}
                            size="small"
                            value={value}
                            min={min}
                            max={max}
                            step={step}
                            disabled={disabled}
                            disableSwap
                            onChange={handleSliderChange}
                            valueLabelDisplay="auto"
                            valueLabelFormat={(v: number) => formatBound(v)}
                            getAriaLabel={(index) => (index === 0 ? "縦軸の下限" : "縦軸の上限")}
                            getAriaValueText={(v) => formatBound(v)}
                            sx={{
                                width: 220,
                                py: 1.5,
                                "& .MuiSlider-rail": { height: 8, borderRadius: 4, opacity: 0.24 },
                                "& .MuiSlider-track": {
                                    height: 8,
                                    borderRadius: 4,
                                    // 平行移動できることを掴める形で示す。
                                    cursor: canPan ? (panning ? "grabbing" : "grab") : "default",
                                },
                                // つまみは白リングで縁取り、掴める点として目立たせる
                                // （::after は MUI のタッチ領域なので触らない）。
                                "& .MuiSlider-thumb": {
                                    width: 18,
                                    height: 18,
                                    border: 2,
                                    borderStyle: "solid",
                                    borderColor: "background.paper",
                                    "&:hover, &.Mui-focusVisible": {
                                        boxShadow: "0 0 0 6px rgba(25, 118, 210, 0.16)",
                                    },
                                    "&.Mui-active": {
                                        width: 22,
                                        height: 22,
                                        boxShadow: "0 0 0 10px rgba(25, 118, 210, 0.16)",
                                    },
                                },
                            }}
                        />
                    </Tooltip>

                    <TextField
                        size="small"
                        variant="standard"
                        disabled={disabled}
                        value={upperText ?? (bounds ? formatBound(value[1]) : "-")}
                        onChange={(e) => setUpperText(e.target.value)}
                        onFocus={() => setUpperText(bounds ? formatBound(value[1]) : "")}
                        onBlur={commitUpper}
                        onKeyDown={(e) => handleBoundKeyDown(e, () => setUpperText(null))}
                        slotProps={{ htmlInput: { "aria-label": "縦軸上限を入力" } }}
                        sx={readoutInputSx}
                    />
                </Box>

                <Typography variant="caption" color="text.disabled" sx={{ lineHeight: 1.2 }}>
                    {bounds
                        ? `全域 ${formatBound(min)} 〜 ${formatBound(max)}・中央をドラッグで範囲を移動`
                        : "描画中のデータがありません"}
                </Typography>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Tooltip title="縦軸範囲を上へ移動">
                    <span>
                        <IconButton
                            size="small"
                            aria-label="縦軸範囲を上へ移動"
                            disabled={atUpperEdge}
                            onClick={() => nudge(NUDGE_STEPS)}
                            sx={{ p: 0.25 }}
                        >
                            <KeyboardArrowUpIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="縦軸範囲を下へ移動">
                    <span>
                        <IconButton
                            size="small"
                            aria-label="縦軸範囲を下へ移動"
                            disabled={atLowerEdge}
                            onClick={() => nudge(-NUDGE_STEPS)}
                            sx={{ p: 0.25 }}
                        >
                            <KeyboardArrowDownIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
        </Paper>
    );
};
